import React from "react";
import { Trash2 } from "lucide-react";
import { SelectedProduct } from "../../OfflineOrder/types";

interface OrderTableProps {
  selectedProducts: SelectedProduct[];
  handleQuantityInput: (compositeId: string, strips: string, tablets: string, noOfItemsInPack: number) => void;
  onUpdateDiscount: (productId: string, discount: number) => void;
  handleRemoveProduct: (compositeId: string) => void;
}

const OrderTable: React.FC<OrderTableProps> = ({
  selectedProducts,
  handleQuantityInput,
  onUpdateDiscount,
  handleRemoveProduct,
}) => {
  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateStripAndTabletCounts = (selectedQuantity: number, noOfItemsInPack: number) => {
    const stripCount = Math.floor(selectedQuantity / noOfItemsInPack);
    const tabletCount = selectedQuantity % noOfItemsInPack;
    return { stripCount, tabletCount };
  };

  // Local state to hold the input values as strings, initialized when product is added
  // This prevents the '0' issue when completely clearing the field
  const handleInputChange = (product: SelectedProduct, field: 'strips' | 'tablets', value: string) => {
    if (value !== "" && !/^\d*$/.test(value)) return; // Allow empty string or digits

    const { stripCount, tabletCount } = calculateStripAndTabletCounts(
      product.selectedQuantity,
      product.noOfItemsInPack
    );

    const currentStrips = field === 'strips' ? value : stripCount.toString();
    const currentTablets = field === 'tablets' ? value : tabletCount.toString();

    handleQuantityInput(product.compositeId, currentStrips, currentTablets, product.noOfItemsInPack);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 flex-1 flex flex-col min-h-[400px]">
      <h2 className="text-lg font-bold text-gray-800 mb-4 -mt-1">Order</h2>

      {/* Table Header */}
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_2fr_1fr_0.5fr] gap-2 text-xs font-semibold text-gray-500 border-b border-gray-200 pb-2 mb-2 w-full pr-2">
        <div>Name</div>
        <div>Batch</div>
        <div>Expiry</div>
        <div>MRP</div>
        <div className="text-center">Strips</div>
        <div className="text-center">Tablets</div>
        <div>Discount (%)</div>
        <div>Total</div>
        <div className="text-center">Action</div>
      </div>

      {/* Table Rows */}
      {selectedProducts.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-12 text-gray-400 text-sm">
          No medicines added yet. Click <b>"Add Medicines"</b> to get started.
        </div>
      ) : (
        <div
          className="flex-1 overflow-y-auto w-full pr-2"
          style={{ scrollbarWidth: "thin", minHeight: "200px" }}
        >
          {selectedProducts.map((product) => {
            const { stripCount, tabletCount } = calculateStripAndTabletCounts(
              product.selectedQuantity,
              product.noOfItemsInPack
            );
            const finalPrice =
              product.perPackPrice *
              (1 - (product.sellingDiscount || 0) / 100) *
              product.selectedQuantity;
            const daysLeft = getDaysRemaining(product.expiry_date);
            const maxDiscount = product.discount;

            return (
              <div
                key={product.compositeId}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_2fr_1fr_0.5fr] gap-2 items-center py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors w-full"
              >
                {/* Name */}
                <div className="flex flex-col min-w-0 pr-2">
                  <div className="text-[13px] font-medium text-gray-800 truncate" title={product.product_name}>
                    {product.product_name}
                  </div>
                  <div className="text-[11px] text-gray-400 truncate" title={product.manufacturer_name}>
                    {product.manufacturer_name}
                  </div>
                </div>

                {/* Batch */}
                <div className="text-[11px] text-gray-600 truncate">{product.batch_number}</div>

                {/* Expiry */}
                <div>
                  <span
                    className={`text-[11px] font-medium whitespace-nowrap ${
                      daysLeft !== null && daysLeft < 30 ? "text-red-500" : "text-gray-600"
                    }`}
                  >
                    {daysLeft !== null
                      ? daysLeft > 0
                        ? `${daysLeft} days`
                        : "Expired"
                      : "N/A"}
                  </span>
                </div>

                {/* MRP */}
                <div className="text-[12px] text-gray-700 font-medium whitespace-nowrap">
                  ₹ {product.mrp?.toFixed(2)}
                </div>

                {/* Strips Input */}
                <div className="flex justify-center px-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={stripCount || ""}
                    onChange={(e) => handleInputChange(product, 'strips', e.target.value)}
                    className="w-12 px-2 py-1 text-center border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 placeholder-transparent bg-white shadow-sm"
                  />
                </div>

                {/* Tablets Input */}
                <div className="flex justify-center px-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={tabletCount || ""}
                    onChange={(e) => handleInputChange(product, 'tablets', e.target.value)}
                    className="w-12 px-2 py-1 text-center border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 placeholder-transparent bg-white shadow-sm"
                  />
                </div>

                {/* Discount */}
                <div className="px-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={product.sellingDiscount || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                        onUpdateDiscount(product.compositeId, parseFloat(val) || 0);
                      }
                    }}
                    placeholder={`Max ${Math.max(0, maxDiscount).toFixed(2)}%`}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>

                {/* Total */}
                <div className="text-[13px] font-bold text-green-700 whitespace-nowrap">
                  ₹ {finalPrice.toFixed(2)}
                </div>

                {/* Action */}
                <div className="flex justify-center">
                  <button
                    onClick={() => handleRemoveProduct(product.compositeId)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderTable;
